/**
 * QHS SidePanel — right-anchored slide-in surface for "create new
 * entity from a list" flows. Per `design-system.html` Form pattern
 * matrix: full-screen modals over forms are not allowed; side panels
 * keep the underlying list visible so the user retains context.
 *
 * Anatomy mirrors the existing centered `Dialog`: pinned `header` and
 * `footer` slots wrap a scrollable body. Implementation is a thin
 * wrapper around MUI Drawer (anchor=right) skinned with QHS tokens.
 *
 * Width:
 *   - Default 520 px (mirrors `Dialog` maxWidth). Caps at
 *     `100vw - 32px`.
 *   - When `resizable` is set, a 4px drag-handle on the panel's left
 *     edge lets the user widen / narrow the panel within
 *     [minWidth, maxWidth]. Pointer Events (mouse + touch) and
 *     keyboard (←/→/Home/End on the focused handle) are supported.
 *   - When `storageKey` is set, the resized width is persisted to
 *     localStorage under `qhs:side-panel-width:<storageKey>` so
 *     re-opening the panel restores the user's choice.
 *
 * Animation: 240ms slide-from-right + fade-in scrim, matching the
 * existing dialog enter/exit cadence.
 *
 * Unsaved-changes guard: when `hasUnsavedChanges` is set, every close
 * attempt (scrim click, ESC, Cancel button via `useSidePanelClose`)
 * funnels through `requestClose`. If there are unsaved edits, a
 * shared `UnsavedChangesModal` blocks the close until the user picks
 * Leave (commits the close) or Stay (cancels it). Cancel buttons in
 * consumer footers must route through the `useSidePanelClose` hook so
 * they pass through the same gate as the Drawer-side close paths.
 *
 *   <SidePanel
 *     open={open}
 *     onClose={close}
 *     header={<DialogHeader>...</DialogHeader>}
 *     footer={<DialogFooter>...</DialogFooter>}
 *     resizable
 *     minWidth={600}
 *     maxWidth={960}
 *     storageKey='create-user'
 *     hasUnsavedChanges={dirty}
 *     unsavedConfirm={{
 *       title: t.unsavedChanges.title,
 *       message: t.unsavedChanges.userCreate,
 *       confirmLabel: t.unsavedChanges.discardButton,
 *       cancelLabel: t.unsavedChanges.keepEditingButton,
 *     }}
 *   >
 *     {body}
 *   </SidePanel>
 */
import { createContext, useCallback, useContext, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import { styled } from '../styled';
import UnsavedChangesModal from '../UnsavedChangesModal';
import useResizableWidth from './useResizableWidth';
import ResizeHandle from './ResizeHandle';

const SidePanelCloseContext = createContext(null);

/**
 * Returns the SidePanel's gated close requester. Cancel buttons rendered
 * inside `header` / `footer` / `children` should call this instead of
 * the consumer's local close handler so they participate in the
 * `hasUnsavedChanges` confirm flow alongside scrim / ESC.
 */
export const useSidePanelClose = () => {
  const ctx = useContext(SidePanelCloseContext);
  if (!ctx) {
    throw new Error('useSidePanelClose must be used inside <SidePanel>');
  }
  return ctx;
};

const PanelRoot = styled('div', {
  shouldForwardProp: prop => prop !== '$width',
})(({ $width }) => ({
  position: 'relative',
  width: $width,
  maxWidth: 'calc(100vw - 32px)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--qhs-surface)',
  boxShadow: 'var(--qhs-sh-4)',
}));

const HeaderSlot = styled('div', {
  shouldForwardProp: prop => prop !== '$divider',
})(({ $divider }) => ({
  flexShrink: 0,
  borderBottom: $divider ? '1px solid var(--qhs-border)' : 'none',
}));

const BodySlot = styled('div')({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
});

const FooterSlot = styled('div', {
  shouldForwardProp: prop => prop !== '$divider',
})(({ $divider }) => ({
  flexShrink: 0,
  borderTop: $divider ? '1px solid var(--qhs-border)' : 'none',
  background: 'var(--qhs-surface)',
}));

const paperSx = {
  background: 'transparent',
  boxShadow: 'none',
  border: 'none',
};

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {React.ReactNode} [props.header]
 * @param {React.ReactNode} [props.footer]
 * @param {React.ReactNode} props.children  Scrolling body slot.
 * @param {number} [props.width=520]        Initial / default width.
 * @param {boolean} [props.resizable=false] Enables drag-handle.
 * @param {number} [props.minWidth=520]     Lower clamp for resize.
 * @param {number} [props.maxWidth=960]     Upper clamp for resize.
 * @param {string} [props.storageKey]       Persists resized width to
 *   localStorage under `qhs:side-panel-width:<storageKey>` when set.
 *   Without this, resize is ephemeral.
 * @param {string} [props.ariaLabel]
 * @param {boolean} [props.hasUnsavedChanges=false]
 *   When true, every close attempt is intercepted and shows the
 *   unsaved-changes confirm modal. Requires `unsavedConfirm` to be
 *   set.
 * @param {{ title: string, message: string, confirmLabel: string,
 *   cancelLabel: string }} [props.unsavedConfirm]
 *   Localised copy for the unsaved-changes confirm modal. `confirm`
 *   is the destructive action ("Leave"); `cancel` keeps the panel
 *   open ("Stay").
 */
const SidePanel = ({
  open,
  onClose,
  header,
  footer,
  children,
  width = 520,
  resizable = false,
  minWidth = 520,
  maxWidth = 960,
  storageKey,
  ariaLabel,
  hasUnsavedChanges = false,
  unsavedConfirm,
}) => {
  const { width: dynamicWidth, onPointerDown, onKeyDown } = useResizableWidth({
    enabled: resizable,
    defaultWidth: width,
    minWidth,
    maxWidth,
    storageKey,
  });

  const effectiveWidth = resizable ? dynamicWidth : width;

  const [confirmOpen, setConfirmOpen] = useState(false);

  const requestClose = useCallback(() => {
    if (hasUnsavedChanges && unsavedConfirm) {
      setConfirmOpen(true);
      return;
    }
    onClose?.();
  }, [hasUnsavedChanges, unsavedConfirm, onClose]);

  const handleDiscard = useCallback(() => {
    setConfirmOpen(false);
    onClose?.();
  }, [onClose]);

  const handleKeepEditing = useCallback(() => setConfirmOpen(false), []);

  return (
    <SidePanelCloseContext.Provider value={requestClose}>
      <Drawer
        anchor='right'
        open={open}
        onClose={requestClose}
        PaperProps={{ sx: paperSx, 'aria-label': ariaLabel }}
        transitionDuration={{ enter: 240, exit: 200 }}
      >
        <PanelRoot $width={effectiveWidth}>
          {resizable && (
            <ResizeHandle
              onPointerDown={onPointerDown}
              onKeyDown={onKeyDown}
              width={effectiveWidth}
              minWidth={minWidth}
              maxWidth={maxWidth}
            />
          )}
          {header && <HeaderSlot $divider={!!children}>{header}</HeaderSlot>}
          {children ? <BodySlot>{children}</BodySlot> : null}
          {footer && <FooterSlot $divider={!!children}>{footer}</FooterSlot>}
        </PanelRoot>
      </Drawer>
      {unsavedConfirm ? (
        <UnsavedChangesModal
          open={confirmOpen}
          title={unsavedConfirm.title}
          message={unsavedConfirm.message}
          discardLabel={unsavedConfirm.confirmLabel}
          keepEditingLabel={unsavedConfirm.cancelLabel}
          onDiscard={handleDiscard}
          onKeepEditing={handleKeepEditing}
        />
      ) : null}
    </SidePanelCloseContext.Provider>
  );
};

export default SidePanel;
